// @spec GHG-STR-048
// @spec GHG-STR-049
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  DollarSign,
  Edit2,
  Leaf,
  Loader2,
  TrendingDown,
  Trash2,
  User,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useMembersSearch } from '@/api/use-members-search';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDeleteReductionInitiative,
  useReductionInitiative,
  useUpdateReductionInitiative,
} from '@/features/ghg/api/use-reduction-initiatives';
import {
  ReductionInitiativeForm,
  type ReductionInitiativeFormValues,
} from '@/features/ghg/components/ReductionInitiativeForm';
import type { InitiativeStatus } from '@/features/ghg/types/reduction-initiative';
import {
  INITIATIVE_CATEGORY_LABELS,
  INITIATIVE_STATUS_VARIANTS,
  getInitiativeCategoryLabels,
  getInitiativeStatusVariants,
} from '@/features/ghg/types/reduction-initiative';
import { formatLocalizedDate } from '@/lib/date-locale';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/ghg/reduction-initiatives/$initiativeId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.initiativeDetails'),
  }),
  component: ReductionInitiativeDetailPage,
});

/**
 * Reduction initiative detail page.
 *
 * Displays comprehensive initiative details including:
 * - Initiative metadata and configuration
 * - Estimated vs actual reduction
 * - Timeline
 * - Edit and delete actions
 */
function ReductionInitiativeDetailPage() {
  const { t } = useTranslation('ghg');
  const categoryLabels = useMemo(() => getInitiativeCategoryLabels(t), [t]);
  const statusVariants = useMemo(() => getInitiativeStatusVariants(t), [t]);
  const navigate = useNavigate();
  const { initiativeId } = Route.useParams();

  const { initiative, isLoading, isError } = useReductionInitiative(initiativeId);
  const updateInitiative = useUpdateReductionInitiative();
  const deleteInitiative = useDeleteReductionInitiative();

  const { data: membersData, isError: membersError } = useMembersSearch({ limit: 50 });
  const members =
    membersData?.members.map((m) => ({
      id: m.userId,
      name: m.displayName ?? m.email,
    })) ?? [];

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const handleBack = () => {
    void navigate({ to: '/ghg/reduction-initiatives' });
  };

  const handleEdit = (data: ReductionInitiativeFormValues) => {
    updateInitiative.mutate(
      {
        initiativeId,
        data: {
          name: data.name,
          description: data.description,
          category: data.category,
          impactedScopes: data.impactedScopes,
          ...(data.impactedActivityGroupIds && { impactedActivityGroupIds: data.impactedActivityGroupIds }),
          estimatedAnnualReduction: data.estimatedAnnualReduction,
          ...(data.estimatedInvestment !== undefined && {
            estimatedInvestment: data.estimatedInvestment,
          }),
          ...(data.estimatedPaybackYears !== undefined && {
            estimatedPaybackYears: data.estimatedPaybackYears,
          }),
          ...(data.plannedStartDate && { plannedStartDate: data.plannedStartDate }),
          ...(data.plannedEndDate && { plannedEndDate: data.plannedEndDate }),
          ...(data.status && { status: data.status as InitiativeStatus }),
          ...(data.actualStartDate && { actualStartDate: data.actualStartDate }),
          ...(data.actualEndDate && { actualEndDate: data.actualEndDate }),
          ...(data.actualAnnualReduction !== undefined && {
            actualAnnualReduction: data.actualAnnualReduction,
          }),
          ...(data.actualInvestment !== undefined && { actualInvestment: data.actualInvestment }),
          ...(data.reductionVerified !== undefined && {
            reductionVerified: data.reductionVerified,
          }),
          ...(data.verificationMethod && { verificationMethod: data.verificationMethod }),
          ownerId: data.ownerId,
        },
      },
      {
        onSuccess: () => {
          toast.success(t('ghg:toast.initiativeUpdatedSuccessfully'));
          setIsEditOpen(false);
        },
        onError: () => {
          toast.error(t('ghg:toast.failedToUpdateInitiative'));
        },
      }
    );
  };

  const handleDelete = () => {
    deleteInitiative.mutate(initiativeId, {
      onSuccess: () => {
        toast.success(t('ghg:toast.initiativeDeletedSuccessfully'));
        void navigate({ to: '/ghg/reduction-initiatives' });
      },
      onError: () => {
        toast.error(t('ghg:toast.failedToDeleteInitiative'));
      },
    });
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (value == null) return '-';
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    return formatLocalizedDate(dateString, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-4xl space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !initiative) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-4xl space-y-6">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('initiativeDetail.back')}
          </Button>
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">
                {t('initiativeDetail.notFound')}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const statusConfig = statusVariants[initiative.status];

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              {t('initiativeDetail.back')}
            </Button>
            <div className="flex items-center gap-3">
              <Leaf className="text-primary h-6 w-6" />
              <h1 className="text-2xl font-semibold">{initiative.name}</h1>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
              <Badge variant="outline">{categoryLabels[initiative.category]}</Badge>
              {initiative.reductionVerified && (
                <Badge variant="secondary" className="gap-1">
                  <CheckCircle className="h-3 w-3" />
                  {t('initiativeDetail.verified')}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsEditOpen(true);
              }}
            >
              <Edit2 className="mr-2 h-4 w-4" />
              {t('initiativeDetail.edit')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setIsDeleteOpen(true);
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('initiativeDetail.delete')}
            </Button>
          </div>
        </div>

        {/* Description */}
        {initiative.description && (
          <Card>
            <CardHeader>
              <CardTitle>{t('initiativeDetail.description')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap">{initiative.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <TrendingDown className="h-4 w-4 text-green-600" />
                {t('initiativeDetail.estimatedReduction')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatNumber(initiative.estimatedAnnualReduction)}
              </div>
              <p className="text-muted-foreground text-xs">{t('initiativeDetail.tCO2ePerYear')}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <DollarSign className="h-4 w-4" />
                {t('initiativeDetail.investment')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatNumber(initiative.estimatedInvestment)}
              </div>
              <p className="text-muted-foreground text-xs">
                {initiative.estimatedPaybackYears
                  ? t('initiativeDetail.yearPayback', { years: initiative.estimatedPaybackYears })
                  : t('initiativeDetail.paybackNotSpecified')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <TrendingDown className="h-4 w-4 text-emerald-600" />
                {t('initiativeDetail.actualReduction')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">
                {formatNumber(initiative.actualAnnualReduction)}
              </div>
              <p className="text-muted-foreground text-xs">{t('initiativeDetail.tCO2ePerYearVerified')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Impacted Scopes */}
        <Card>
          <CardHeader>
            <CardTitle>{t('initiativeDetail.impactedScopes')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {initiative.impactedScopes.map((scope) => (
                <Badge key={scope} variant="secondary">
                  {t('initiativeDetail.scopePrefix')} {scope}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {t('initiativeDetail.timeline')}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 text-sm md:grid-cols-2">
            <div>
              <span className="text-muted-foreground">{t('initiativeDetail.plannedStart')}:</span>
              <span className="ml-2">{formatDate(initiative.plannedStartDate)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('initiativeDetail.plannedEnd')}:</span>
              <span className="ml-2">{formatDate(initiative.plannedEndDate)}</span>
            </div>
            {initiative.actualStartDate && (
              <div>
                <span className="text-muted-foreground">{t('initiativeDetail.actualStart')}:</span>
                <span className="ml-2">{formatDate(initiative.actualStartDate)}</span>
              </div>
            )}
            {initiative.actualEndDate && (
              <div>
                <span className="text-muted-foreground">{t('initiativeDetail.actualEnd')}:</span>
                <span className="ml-2">{formatDate(initiative.actualEndDate)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Verification */}
        {initiative.reductionVerified && initiative.verificationMethod && (
          <Card>
            <CardHeader>
              <CardTitle>{t('initiativeDetail.verification')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{initiative.verificationMethod}</p>
            </CardContent>
          </Card>
        )}

        {/* Metadata */}
        <Card>
          <CardHeader>
            <CardTitle>{t('initiativeDetail.details')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 text-sm md:grid-cols-2">
            <div>
              <span className="text-muted-foreground">{t('initiativeDetail.created')}:</span>
              <span className="ml-2">{formatDate(initiative.createdAt)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('initiativeDetail.lastUpdated')}:</span>
              <span className="ml-2">{formatDate(initiative.updatedAt)}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="text-muted-foreground h-4 w-4" />
              <span className="text-muted-foreground">{t('initiativeDetail.owner')}:</span>
              <span className="ml-1">{initiative.owner.name}</span>
            </div>
            {initiative.targetName && (
              <div>
                <span className="text-muted-foreground">{t('initiativeDetail.linkedTarget')}:</span>
                <span className="ml-2">{initiative.targetName}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('initiativeDetail.editTitle')}</DialogTitle>
            <DialogDescription>{t('initiativeDetail.editDescription')}</DialogDescription>
          </DialogHeader>
          <ReductionInitiativeForm
            initiative={initiative}
            onSubmit={handleEdit}
            onCancel={() => {
              setIsEditOpen(false);
            }}
            isSubmitting={updateInitiative.isPending}
            members={members}
            membersError={membersError}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('initiativeDetail.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('initiativeDetail.deleteDescription', { name: initiative.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('initiativeDetail.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteInitiative.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              {t('initiativeDetail.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
